/**
 * The conversation, on v1.5's parts.
 *
 * `Workspace.stories.tsx`'s `Thread` is the reference and this is that function with the fixture
 * pulled out: `ConversationColumn` holding `DayDivider`s and `ConversationTurn`s, each turn a
 * `UserMessage` and whatever the agent did about it. The two rules the story states in passing are
 * kept because both are load-bearing — `padded={false}` on a `UserMessage` inside a turn (the turn's
 * slot already pays the 6px, and both paying it puts 12 where the design draws 6), and
 * `spaced={false}` on a turn that opens on a divider (a first turn cannot be found from CSS, so the
 * caller is the only one who knows).
 *
 * **Memoised, and the prop identities matter.** Both existing call sites of the real chat carry the
 * same warning: an unstable prop re-renders the whole thread once per animation frame while a reply
 * streams. Every prop below is either a primitive or something the pane holds still on purpose —
 * which is why `artifactItem` is a memoised lookup and not the directory's array, whose identity
 * changes on every blob refetch.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import {
  ConversationColumn,
  ConversationTurn,
  DayDivider,
  MessageActions,
  SupersededQuote,
  TaskCard,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import type { DirectoryItem } from '~/design-system/components/v1_5/Directory';
import { track } from '~/lib/posthog';
import type { PlanningChatMessage } from '~/types/planning';
import { QuestionBlock } from './question';
import { TurnActivity } from './activity/TurnActivity';
import { ThinkingIndicator } from './activity/ThinkingIndicator';
import { WorkSummary } from './activity/WorkSummary';
import { blocksFromMessage, supersededFromMessage } from './rehydrate';
import { copyText } from './thread/clipboard';
import { renderAnswer } from './thread/RichProse';
import { LiveProse } from './thread/LiveProse';
import { ThreadArtifactCard } from './thread/ThreadArtifactCard';
import { UserTurn } from './thread/UserTurn';
import type { TurnBlock } from './turnModel';
import { artifactCompletionText, receiptDurationSec, receiptStartedAt, resolveArtifactReceipts, type ArtifactOutcome, type ArtifactReceipt } from './artifactReceipts';
import { genRecords, genServerVersion, genVersion, subscribeGen } from '../generate/genBus';

export interface DeckThreadProps {
  theme: ChatTheme;
  /**
   * The column's cap, measured off the pane by `DeckAgentPane`.
   *
   * Not the artboard's 400: that is the width the boards were drawn in, and `ConversationColumn`'s
   * own doc calls the prop a cap that a wider panel may legitimately raise. Held at 400 the thread
   * ignored the seam entirely.
   */
  columnWidth?: number;
  /** Oldest first, straight from the shared cache. */
  messages: PlanningChatMessage[];
  /**
   * The turn currently streaming, if any. Settled turns are rebuilt from their persisted parts, so
   * this is the ONE message whose blocks do not come from `blocksFromMessage`.
   */
  live: { messageId: string; blocks: TurnBlock[]; elapsedSec: number; quietMs?: number } | null;
  /** The container owns selection, so the card asks rather than decides. */
  onOpenArtifact: (key: string) => void;
  onOpenResource?: (key: string) => void;
  /**
   * The directory's row for a `slide:N` key.
   *
   * A function and not the list: `content.items.artifacts` is rebuilt on every blob refetch, so a
   * thread handed the array would re-render in full each time — once per artifact during a
   * generation run, on the component whose own docblock is about prop identity. Optional, so the
   * thread still renders (with the numeric fallback) for a caller with no deck to hand.
   */
  artifactItem?: (key: string) => DirectoryItem | undefined;
  /**
   * The thumbnail to draw a user turn's annotation pins back onto, by slide id.
   *
   * Threaded through untouched, for the same reason `artifactItem` is a function: `UserTurn` is
   * memoised, and a lookup minted in this body would be a new identity on every frame of a streaming
   * reply — re-rendering every bubble in the thread to redraw pins that did not move. Optional, and
   * absent it the reference still draws; the pins are the annotation and the picture is the ground.
   */
  artifactThumb?: (slideId: number | string) => string | undefined;
  /**
   * The two halves of a pressable mention: every name one could be wearing, and what each opens.
   *
   * A FUNCTION and a stable array rather than the deck's rows, for the reason `artifactItem` carries
   * — the item arrays change identity on every blob refetch and the turns are memoised on their
   * props. Absent, a mention in a sent bubble stays prose, which is right for a thread with no deck
   * behind it.
   */
  mentionNames?: readonly string[];
  mentionTarget?: (name: string) => (() => void) | undefined;
  /**
   * Canvas slide ids each assistant turn touched, keyed by message id.
   *
   * It is read out of `editCanvasDispatch` and the host's create callback, then kept in a small
   * per-conversation browser receipt. That is the association between a response and its artifact
   * card; the deck blob remains the source of truth for the artifact itself.
   */
  artifacts?: Record<string, number[]>;
  /** Receipts with the prompt/time anchor needed after optimistic ids become server ids. */
  artifactReceipts?: ArtifactReceipt[];
  /**
   * The one assistant message that may be retried — the last one, and `null` while a run is in
   * flight. There is no per-message retry endpoint: the server regenerates the tail of the
   * conversation, so the glyph on an older turn would rewrite a different one.
   */
  retryableId?: string | null;
  onRetry?: () => void;
}

/** One exchange: what was said, and everything that came back before the next thing was said. */
interface Turn {
  id: string;
  user?: PlanningChatMessage;
  agent: PlanningChatMessage[];
  at: Date;
}

/**
 * Flat message list into turns.
 *
 * A user message opens a turn and every assistant message after it belongs to that one. The leading
 * case is real and has to be handled: a conversation can open on the agent (a deck created from a
 * prompt elsewhere already has a reply in it before this pane is ever shown), which is the
 * `user`-less turn `ConversationTurn` documents as `Task reports back`.
 */
function toTurns(messages: PlanningChatMessage[]): Turn[] {
  const turns: Turn[] = [];
  for (const message of messages) {
    const at = message.createdAt instanceof Date ? message.createdAt : new Date(message.createdAt);
    if (message.role === 'user' || turns.length === 0) {
      turns.push(
        message.role === 'user'
          ? { id: message.id, user: message, agent: [], at }
          : { id: message.id, agent: [message], at },
      );
      continue;
    }
    turns[turns.length - 1].agent.push(message);
  }
  return turns;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * Whether the thread draws a CARD for the thing a turn made — the artifact tile, the document card.
 *
 * OFF (Beka, 2026-09-23: "no need for this card to show, it's enough to open that specific
 * document … same goes with artifacts"). The card was the thread's way of saying "this exists now,
 * press here to see it", and it is redundant once the workspace opens the thing on its own: a document
 * the agent writes opens in the preview (`useWorkspaceResources`), and an artifact the agent starts on
 * opens the moment it starts (`followAgent`). So the preview IS the result, and a card under the
 * answer pointing at the preview is the same fact said twice.
 *
 * Hidden rather than deleted, the same way the Worked-for tally is: a card is still the one way back to
 * a result from a turn you scroll up to days later, and that is a call worth being able to reverse
 * with one boolean. The ROWS stay either way — **Wrote a document**, **Created an artifact**,
 * **Edited Artifact 3** — because they are the record of what happened, not a link to it.
 *
 * Only cards. A document the agent DELETED never had one (there is nothing to open), and the Goal
 * widget is a different object that this does not reach.
 */
const RESULT_CARDS: boolean = false;

/**
 * How long a live turn's stream stays silent before prose stops counting as a sign of life. Longer
 * than `useSmoothText`'s 250ms catch-up, so a reveal still finishing never trips it; far shorter than
 * the 20 to 40 seconds a model spends generating an artifact call.
 */
const QUIET_MS = 1200;
/** The live wording an artifact row wears while its host works; anything else is already its finished verb. */
const RUNNING_ARTIFACT_LABEL = /^(Writing|Creating|Editing|Updating|Preparing|Generating) /;

/**
 * `ConversationTurn`'s own 16px, moved up onto the quote that opens the exchange.
 *
 * 14I1-0 draws the superseded quote and the bubble that overrode it ONE column gap apart — 12, the
 * only separator — and the quote is the first thing in that exchange, not a sibling of the turn
 * before it. Left alone the turn keeps its `spaced` and pays 16 in the wrong place: 28px between the
 * quote and its own message, 12 up to the previous turn, which reads as the quote belonging to the
 * exchange above. `SupersededQuote` takes no `className`, so the margin sits on a wrapper here and
 * the turn below drops its own; see the push site.
 */
const SUPERSEDED_OPENS_TURN: CSSProperties = { marginTop: 16 };

export function withArtifactProgress(
  block: Extract<TurnBlock, { kind: 'activity' }>,
  records: ReturnType<typeof genRecords>,
  messageId: string,
  receiptIds: string[] = [],
  outcomes: ArtifactOutcome[] = [],
): Extract<TurnBlock, { kind: 'activity' }> {
  if (block.tool !== 'artifact' && block.tool !== 'canvas-edit') return block;

  const matching = records
    .filter(({ record }) => record.messageId === messageId || (record.messageId && receiptIds.includes(record.messageId)))
    .map(({ record }) => record);
  const statuses = matching.length ? matching : outcomes.map((outcome) => ({
    state: outcome.status === 'failed' ? 'failed' : 'done', operation: outcome.operation, phase: '',
  }));
  if (!statuses.length) {
    // A hard refresh has no tab-local generation receipt. Persisted artifact activity can still
    // carry the old live label, so settle that generic wording from the action type and remove any
    // old body here as well. The card below is the artifact's durable output; this row is its verb.
    // Only a row still wearing its RUNNING wording is settled here: a row the backend already finished
    // ("Read Artifact 3", "Created an artifact") keeps its own words, or a read would say "wrote"
    // (Guri, 2026-09-14, DECK-1691).
    if (block.lifecycle !== 'complete' || !RUNNING_ARTIFACT_LABEL.test(block.label)) return block;
    return {
      ...block,
      label: block.tool === 'canvas-edit' ? 'Updated the artifact' : 'Wrote the artifact',
      body: { kind: 'none' },
    };
  }

  const failed = statuses.some((record) => record.state === 'failed');
  const finished = statuses.every((record) => record.state === 'done' || record.state === 'resolving');
  const active = statuses.filter((record) => record.state !== 'done' && record.state !== 'failed' && record.state !== 'resolving');
  const phaseRecord = active[active.length - 1] ?? statuses[statuses.length - 1];
  const label =
    failed
      ? 'Artifact generation failed'
      : finished
        ? phaseRecord.operation === 'edit'
          ? 'Updated the artifact'
          : 'Wrote the artifact'
        : phaseRecord.phase === 'Thinking' || phaseRecord.phase === 'Writing the artifact'
          ? 'Preparing the artifact'
          : phaseRecord.phase;
  const lifecycle =
    failed
      ? 'failed'
      : finished || phaseRecord.state === 'resolving'
        ? 'complete'
        : phaseRecord.state === 'finishing'
          ? 'updating'
          : 'running';

  // The artifact card is the progress widget. Keeping the old phase queue here duplicated the
  // directory UI inside the work summary and left a stale "Writing the artifact" skeleton behind
  // after the document had settled. Work rows carry only the concise current/final status.
  return { ...block, label, lifecycle, body: { kind: 'none' } };
}

function isGenerationActive(record: ReturnType<typeof genRecords>[number]['record']): boolean {
  return record.state !== 'failed' && record.state !== 'done' && record.state !== 'resolving';
}

/** `Today`, `Yesterday`, then the date itself — the three labels `DayDivider` is drawn with. */
function dayLabel(at: Date, now: Date): string {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const stamp = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
  if (stamp === midnight) return 'Today';
  if (stamp === midnight - DAY) return 'Yesterday';
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

/**
 * A rating, going somewhere.
 *
 * The shipping chat's thumbs are a local `useState` that reports to nothing at all, which is the
 * dead button `MessageActions` refuses to draw — "a button is drawn when, and only when, there is
 * something behind it". PostHog is already initialised for the app and is a real sink, so the glyph
 * records a real fact even though the design gives it no pressed state to show for it. Guarded,
 * because an ad blocker taking the library down must not take a click handler with it.
 */
function rate(messageId: string, verdict: 'up' | 'down') {
  track('planning_chat_message_rated', { messageId, verdict, surface: 'deck15' });
}

/**
 * How long a live line must hold before the Thinking row stands in for it. Grok and DeepSeek often
 * finish the next sentence 50 to 250 ms after the last, and a row flashing between two sentences reads
 * as a glitch (DECK-1692).
 */
const HELD_LINE_MS = 200;

export const DeckThread = memo(function DeckThread({
  theme,
  messages,
  columnWidth,
  live,
  onOpenArtifact,
  onOpenResource,
  artifactItem,
  artifactThumb,
  mentionNames,
  mentionTarget,
  artifacts,
  artifactReceipts,
  retryableId,
  onRetry,
}: DeckThreadProps) {
  useSyncExternalStore(subscribeGen, genVersion, genServerVersion);
  const generationRecords = genRecords();
  const turns = useMemo(() => toTurns(messages), [messages]);
  const now = useMemo(() => new Date(), []);
  const receipts = useMemo<ArtifactReceipt[]>(
    () =>
      artifactReceipts?.length
        ? artifactReceipts
        : Object.entries(artifacts ?? {}).map(([messageId, slideIds]) => ({
            messageId,
            slideIds,
            startedAt: receiptStartedAt(messageId),
          })),
    [artifactReceipts, artifacts],
  );
  const receiptsByMessage = useMemo(() => resolveArtifactReceipts(messages, receipts), [messages, receipts]);

  /**
   * The live prose block that has held for HELD_LINE_MS: text arrived, but its line is not complete, so
   * `LiveProse` draws nothing for it. The turn's own Thinking row stands in its place until it plays.
   */
  const [heldLine, setHeldLine] = useState<string | null>(null);
  const heldTimers = useRef(new Map<string, number>());
  const lineHeld = useCallback((key: string, holding: boolean) => {
    const timers = heldTimers.current;
    window.clearTimeout(timers.get(key));
    timers.delete(key);
    if (holding) {
      timers.set(key, window.setTimeout(() => {
        timers.delete(key);
        setHeldLine(key);
      }, HELD_LINE_MS));
    } else {
      setHeldLine((current) => (current === key ? null : current));
    }
  }, []);
  useEffect(() => {
    const timers = heldTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const rows: ReactNode[] = [];
  let day = '';

  turns.forEach((turn) => {
    const label = dayLabel(turn.at, now);
    // A turn that opens on a divider is the one turn CSS could never find, so it says so itself.
    const opensDay = label !== day;
    if (opensDay) {
      day = label;
      rows.push(<DayDivider key={`day-${label}`} label={label} theme={theme} />);
    }

    /**
     * The decision this turn overrode, above the message that overrode it.
     *
     * `14I1-0` is the one artboard where something precedes the user bubble, and that is the whole
     * point of it — "an earlier call the later one overrode. it stays, or the thread lies." It sits
     * in the column rather than inside the turn because the turn's `.user` slot right-aligns
     * everything in it and this block spans; the column's own 12px gap is the distance drawn.
     *
     * Which is only true if the turn below stops paying its own 16 — the quote OPENS this exchange,
     * so the wrapper takes the margin and the turn goes unspaced. See `SUPERSEDED_OPENS_TURN`.
     */
    const superseded = turn.agent.map(supersededFromMessage).find(Boolean);
    if (superseded) {
      rows.push(
        <div
          key={`${turn.id}-superseded`}
          style={opensDay ? undefined : SUPERSEDED_OPENS_TURN}
        >
          <SupersededQuote theme={theme}>{superseded}</SupersededQuote>
        </div>,
      );
    }

    const blocks: ReactNode[] = [];
    const workBlocks: TurnBlock[] = [];
    const workNodes: ReactNode[] = [];
    const historyNodes: ReactNode[] = [];
    const liveNodes: ReactNode[] = [];
    const artifactNodes: ReactNode[] = [];
    const drawnArtifacts = new Set<number>();
    const turnReceipts = turn.agent.flatMap((message) => receiptsByMessage.get(message.id) ?? []);
    const turnReceiptSlideIds = [...new Set(turnReceipts.flatMap((receipt) => receipt.slideIds))];
    const responseNodes: ReactNode[] = [];
    /**
     * The same trailing run as `responseNodes`, in the copies that went into `historyNodes`.
     *
     * Two arrays because the run is drawn TWICE, at two tones. Under the turn it is the answer and
     * is full white; inside `Worked for …` it is the record of how the turn went and stays subtle,
     * along with the commentary from before the last tool call, which never leaves the fold at all.
     * A React element cannot be re-toned after the fact, so the two are separate elements built at
     * the same moment.
     *
     * This one exists so the fold's filter below can still be an IDENTITY test: it has to remove the
     * nodes it is actually holding, and those are these, not the white ones drawn under the turn.
     */
    const responseHistory: ReactNode[] = [];
    const responseTexts: string[] = [];
    let hasLiveIndicator = false;
    /** The streaming turn's last block, when it is a paragraph the stream is playing. */
    let liveTailProse: string | undefined;
    const turnRecords = generationRecords.filter(
      ({ record }) => turn.agent.some((message) => record.messageId === message.id) ||
        turnReceipts.some((receipt) => receipt.messageId === record.messageId),
    );
    const turnOutcomes = [...new Map([
      ...turnReceipts.flatMap((receipt) => receipt.outcomes ?? []),
      ...turnRecords.flatMap(({ slideId, record }): ArtifactOutcome[] => !isGenerationActive(record) && record.finishedAt ? [{
        slideId, operation: record.operation, status: record.state === 'failed' ? 'failed' : 'complete',
        title: record.title, finishedAt: record.finishedAt, summary: record.summary, notes: record.notes,
      }] : []),
    ].map((outcome) => [outcome.slideId, outcome])).values()];
    const artifactInterrupted = turnReceipts.some((receipt) => receipt.interrupted);
    const pendingDispatch = turnReceipts.some((receipt) => receipt.dispatched && !receipt.interrupted &&
      (!receipt.slideIds.length || receipt.slideIds.some((id) => !turnOutcomes.some((outcome) => outcome.slideId === id))));
    const turnGenerationActive = turnRecords.some(({ record }) => isGenerationActive(record)) || pendingDispatch;
    const completedAnswer = artifactCompletionText(turnOutcomes);
    const interruptedAnswer = artifactInterrupted ? 'Artifact generation was interrupted before a saved result could be confirmed. Please try again.' : '';
    /** The turn is still being written, so it does not get its closing action row yet. */
    let streaming = false;

    turn.agent.forEach((message) => {
      /**
       * One source per message. The streaming turn's blocks come from the builder; every settled
       * turn is rebuilt from its persisted parts, which is what stops a turn whose substance was six
       * tool rows from reloading as an empty bubble.
       */
      const messageReceipts = receiptsByMessage.get(message.id) ?? [];
      const isLive = Boolean(live && (live.messageId === message.id || messageReceipts.some((receipt) => receipt.messageId === live.messageId)));
      if (isLive) streaming = true;
      const turnBlocks = isLive && live ? live.blocks : blocksFromMessage(message);
      const messageReceiptSlideIds = [...new Set(messageReceipts.flatMap((receipt) => receipt.slideIds))];
      const messageGenerationRecords = generationRecords.filter(
        ({ record }) => record.messageId === message.id || messageReceipts.some((receipt) => receipt.messageId === record.messageId),
      );
      const messageSlideIds = [...new Set([
        ...turnBlocks.flatMap((block) => block.kind === 'artifact' ? [block.slideId] : []),
        ...messageReceiptSlideIds,
        ...messageGenerationRecords.map(({ slideId }) => slideId),
      ])];
      // No tile unless `RESULT_CARDS` says so: the artifact opens itself. Every branch below already
      // handles an empty list — it is exactly the story path, where the tools return inside the turn
      // and the row draws live in the card's place.
      const messageCards = !RESULT_CARDS ? [] : messageSlideIds.flatMap((slideId) => {
        if (drawnArtifacts.has(slideId)) return [];
        drawnArtifacts.add(slideId);
        const generation = messageGenerationRecords.find((entry) => entry.slideId === slideId);
        return [<ThreadArtifactCard
          key={`${message.id}-art-${slideId}`}
          slideId={slideId}
          theme={theme}
          item={artifactItem?.(`slide:${slideId}`)}
          busy={Boolean(generation ? isGenerationActive(generation.record) : pendingDispatch && artifactItem?.(`slide:${slideId}`)?.meta?.startsWith('queued') !== true && !turnOutcomes.some((outcome) => outcome.slideId === slideId))}
          progressLabel={generation && isGenerationActive(generation.record) ? generation.record.phase : undefined}
          onOpen={onOpenArtifact}
        />];
      });
      artifactNodes.push(...messageCards);
      const notes = messageSlideIds.flatMap((slideId) => {
        const generation = messageGenerationRecords.find((entry) => entry.slideId === slideId);
        return generation?.record.notes ?? messageReceipts.flatMap((receipt) =>
          receipt.outcomes?.find((outcome) => outcome.slideId === slideId)?.notes ?? []);
      }).sort((a, b) => a.at - b.at);
      const noteNodes = notes.flatMap((note) => renderAnswer(note.text, `${message.id}-note-${note.id}`, theme));
      let attachedArtifact = false;
      const attachArtifact = () => {
        if (attachedArtifact) return;
        attachedArtifact = true;
        historyNodes.push(...noteNodes);
        // The widget arrives at dispatch; later narration grows BELOW it, never in its place.
        liveNodes.push(...messageCards, ...noteNodes);
      };

      turnBlocks.forEach((block, blockIndex) => {
        switch (block.kind) {
          case 'prose': {
            // Headings, tables and numbered steps have components — `14DR-0`, "a heading, a table
            // and numbered steps. prose was only ever paragraphs." Everything markdown has no
            // component for stays a paragraph rather than being approximated with one that means
            // something else. Spread and not wrapped: the assistant slot's 12px gap is between
            // BLOCKS, and a fragment holding five of them is one block.
            // Every paragraph this stream wrote plays through `LiveProse` for as long as the turn is live,
            // on one stable key, so a line still playing is never swapped for plain text when a tool row
            // lands under it (DECK-1692). A paragraph without `open` was rebuilt from history (the settled
            // half of a resumed turn) and is painted whole, like every settled turn.
            const proseKey = `${message.id}-${block.id}`;
            const streamed = isLive && block.open !== undefined;
            if (streamed && blockIndex === turnBlocks.length - 1) liveTailProse = proseKey;
            const prose = streamed
              ? [<LiveProse
                  key={`${proseKey}-live`}
                  text={block.text}
                  keyPrefix={proseKey}
                  theme={theme}
                  // Closed by `textBoundary`, `done` or a later block: the rest of it may play.
                  sealed={block.open === false || blockIndex < turnBlocks.length - 1}
                  onHoldingChange={(holding) => lineHeld(proseKey, holding)}
                />]
              : renderAnswer(block.text, proseKey, theme);
            /*
             * The same paragraph again, full white, for the one place it is the ANSWER.
             *
             * Only what stands under the finished turn is full strength (Beka, 2026-09-20: "the last
             * output … should be only the full white"). Everything else this block feeds — the fold's
             * record, and the live column while the turn is still running — keeps the subtle tone it
             * has always had, which is why the STREAM is untouched: a line being written is not yet
             * the last thing said, and brightening it would have meant guessing.
             *
             * A streaming block is its own answer already: `LiveProse` owns the words while they
             * play and there is nothing to draw twice, so it is handed straight through.
             */
            const answerProse = streamed ? prose : renderAnswer(block.text, proseKey, theme, 'default');
            responseNodes.push(...answerProse);
            responseHistory.push(...prose);
            responseTexts.push(block.text);
            historyNodes.push(...prose);
            // Commentary is part of the sequence, including after a tool starts. Deciding whether
            // to show it from FUTURE blocks made the same text disappear/reappear between paints.
            // Only the saved-result summary and Worked recap wait for both streams to finish.
            liveNodes.push(...prose);
            break;
          }

          case 'artifact':
            attachArtifact();
            break;

          case 'task':
            responseNodes.length = 0;
            responseHistory.length = 0;
            responseTexts.length = 0;
            workBlocks.push(block);
            workNodes.push(
              block.state === 'external' ? (
                <TaskCard
                  key={block.id}
                  theme={theme}
                  kind="external"
                  // The only dashed border in eighty artboards, and it is doing semantic work:
                  // "a push is not artifact work — you cannot undo it from here, so it looks
                  // different." `github` is the union's only member today.
                  brand={block.brand ?? 'github'}
                  title={block.label}
                  meta="outside the deck"
                />
              ) : (
                <TaskCard
                  key={block.id}
                  theme={theme}
                  kind={block.state}
                  title={block.label}
                  meta="background task"
                  status={block.state === 'running' ? 'Running in the background' : 'Done'}
                />
              ),
            );
            historyNodes.push(workNodes[workNodes.length - 1]);
            liveNodes.push(workNodes[workNodes.length - 1]);
            if (block.state === 'running') hasLiveIndicator = true;
            break;

          case 'question':
            // The whole life of one ask — the inline chips, the "still asking" beat while the
            // composer holds the card, and the answered receipt. Which of the three it draws is
            // `routeOf`'s call, not this loop's. The chips reach the send path through
            // `QuestionAnswerProvider` in the pane rather than a prop, so this stays memo-safe.
            {
              const question = <QuestionBlock key={block.id} block={block} theme={theme} />;
              responseNodes.push(question);
              liveNodes.push(question);
            }
            break;

          /**
           * Reasoning and tool activity, both through one renderer.
           *
           * `elapsedSec` is only passed for the STREAMING turn, and only that turn draws a clock: a
           * settled "Thought for 9 sec" states a duration the block already carries, while the live
           * row's clock is counting the run and has to come from outside the block.
           */
          case 'thinking':
          case 'activity': {
            if (block.kind === 'activity' && !block.transient) {
              responseNodes.length = 0;
              responseHistory.length = 0;
              responseTexts.length = 0;
            }
            /*
             * A document the agent wrote or changed is a CARD, and with `RESULT_CARDS` off it is not
             * drawn at all — not turned into a row, because the row already exists: the backend's
             * own step (**Wrote a document**, **Updated a document**) is a separate block, and this one
             * arrives after it carrying the title and the key to open. Drawing it as a row would put
             * the same write in the fold twice. The document itself opens in the preview.
             *
             * Skipped AFTER the reset above, so the turn's shape is exactly what it was: prose before
             * this point is still the record and not the answer.
             */
            if (!RESULT_CARDS && block.kind === 'activity' && block.tool === 'document' && block.resourceKey) break;
            // A one-line action event describes the current phase, not a background job. Once
            // another block arrives, keep its row but stop treating that old phase as still live.
            const activity = block.kind === 'activity' && block.transient && blockIndex < turnBlocks.length - 1 &&
              (block.lifecycle === 'running' || block.lifecycle === 'updating')
              ? { ...block, lifecycle: 'complete' as const } : block;
            const workBlock =
              activity.kind === 'activity'
                ? withArtifactProgress(activity, generationRecords, message.id, messageReceipts.map((receipt) => receipt.messageId), messageReceipts.flatMap((receipt) => receipt.outcomes ?? []))
                : block;
            workBlocks.push(workBlock);
            workNodes.push(
              <TurnActivity
                key={`${message.id}-${block.id}`}
                block={workBlock}
                onOpenResource={onOpenResource}
                elapsedSec={isLive ? live?.elapsedSec : undefined}
                theme={theme}
              />,
            );
            historyNodes.push(workNodes[workNodes.length - 1]);
            if (workBlock.kind === 'activity' && workBlock.resourceKey &&
                (workBlock.tool === 'goal' || workBlock.tool === 'document')) {
              artifactNodes.push(workNodes[workNodes.length - 1]);
            }
            if (block.kind === 'activity' && (block.tool === 'artifact' || block.tool === 'canvas-edit')) {
              // On the story path the artifact tools return inside the turn and no card stands in for
              // the row, so the row draws live like any other step. Without this "Creating an artifact"
              // was never on screen while the artifact was written, only the bare mark (DECK-1689).
              //
              // BEFORE `attachArtifact`, which is the order that matters once there is no card at all
              // (`RESULT_CARDS`). The row is then the widget — it carries the generation's live phase —
              // and the generator's notes have to grow UNDER it, the way they grew under the card
              // ("later narration grows BELOW it, never in its place"). Pushed after, the first note
              // landed above the row it was narrating.
              if (!messageCards.length && workBlock.kind === 'activity') {
                liveNodes.push(workNodes[workNodes.length - 1]);
                if (workBlock.lifecycle === 'running' || workBlock.lifecycle === 'updating') hasLiveIndicator = true;
              }
              attachArtifact();
            } else if (workBlock.kind === 'activity') {
              const active = workBlock.lifecycle === 'running' || workBlock.lifecycle === 'updating';
              // The artifact widget already owns its status. Earlier standalone steps still stay.
              if (!(block.kind === 'activity' && block.transient && active && turnGenerationActive)) {
                liveNodes.push(workNodes[workNodes.length - 1]);
                if (active) hasLiveIndicator = true;
              }
            } else if (block.kind === 'thinking' && (!block.live || (isLive && !turnGenerationActive))) {
              liveNodes.push(workNodes[workNodes.length - 1]);
              if (block.live) hasLiveIndicator = true;
            }
            break;
          }

          default:
            break;
        }
      });

      // A freshly minted id may arrive after the chat stream ended, or only through its receipt.
      attachArtifact();
    });

    const busy = streaming || turnGenerationActive;
    const finalAnswer = [completedAnswer, interruptedAnswer].filter(Boolean).join('\n\n');
    if (busy) {
      // Keep acknowledgement → action → finding → next action in order. Only the current work
      // owns a loader; no duplicate Working header or artifact phase row above its widget.
      blocks.push(...liveNodes);
      const tail = live?.blocks[live.blocks.length - 1];
      // No indicator under an OPEN question (the turn is paused on the user), but an answered one
      // is the start of the continuation: the model is working again and the row must say so, or
      // the thread looks dead until its first word (Guri, 2026-09-13, "took 56 seconds to continue").
      const pausedOnUser = tail?.kind === 'question' && tail.status === 'asking';
      // Prose still arriving is its own sign of life; prose the model has finished is not. Once the
      // stream has been quiet for QUIET_MS the mark comes up under the lead-in too, because the model is
      // now generating a tool call the browser cannot see, and that is exactly when the thread used to
      // look finished (Guri, 2026-09-14). An attached turn has no frame clock and always shows it.
      const quiet = live?.quietMs === undefined || live.quietMs >= QUIET_MS;
      // A tail paragraph that has held for HELD_LINE_MS draws nothing yet, so it is no sign of life
      // either: the same one row stands in its place (it is the tail, so this is its place).
      const heldTail = heldLine !== null && heldLine === liveTailProse;
      if (!hasLiveIndicator && !(turnGenerationActive && artifactNodes.length) && (tail?.kind !== 'prose' || quiet || heldTail) && !pausedOnUser) {
        // A thought that just settled stays in view, dimmed, while its tool call is generated, so the
        // row never drops to a bare clock mid-step. The next action or fresh reasoning replaces it.
        const settledThought = tail?.kind === 'thinking' && !tail.live && tail.text.trim() ? tail.text : undefined;
        blocks.push(
          <ThinkingIndicator key={`${turn.id}-waiting`} theme={theme} elapsedSec={live?.elapsedSec ?? 0}
            reasoning={settledThought} stale />,
        );
      }
    } else {
      if (artifactInterrupted) {
        const failed: TurnBlock = { kind: 'activity', id: `${turn.id}-artifact-interrupted`, tool: 'artifact',
          label: 'Artifact generation interrupted', lifecycle: 'failed', body: { kind: 'none' } };
        workBlocks.push(failed);
        const node = <TurnActivity key={failed.id} block={failed} theme={theme} />;
        workNodes.push(node);
        historyNodes.push(node);
      }
      if (workNodes.length > 0) {
        blocks.push(
          <WorkSummary
            key={`${turn.id}-work`}
            blocks={workBlocks}
            artifactCount={turnReceiptSlideIds.length || undefined}
            elapsedSec={receiptDurationSec(turnReceipts.map((receipt) => ({ ...receipt, outcomes: turnOutcomes })))}
          >
            {finalAnswer ? historyNodes : historyNodes.filter((node) => !responseHistory.includes(node))}
          </WorkSummary>,
        );
      }
      blocks.push(
        ...(finalAnswer
          ? renderAnswer(finalAnswer, `${turn.id}-result`, theme, 'default')
          : responseNodes),
      );
      blocks.push(...artifactNodes);
    }

    /**
     * The row that ends a turn — copy, thumbs-up, thumbs-down, retry, in that order and no other.
     *
     * On the last assistant message of a SETTLED turn, which is where all eight closing artboards
     * put it. Retry only on the conversation's last turn: the endpoint regenerates the tail, so the
     * glyph anywhere else would rewrite a different turn than the one it sits under. `retryableId`
     * goes null while a run is in flight, which is how the control disappears mid-run rather than
     * sitting there inert.
     */
    const closing = turn.agent[turn.agent.length - 1];
    if (closing && !busy) {
      const answer = finalAnswer || responseTexts.join('\n\n').trim();
      blocks.push(
        <MessageActions
          key={`${turn.id}-actions`}
          side="assistant"
          theme={theme}
          // The words, not the wire. `content` is what the thread shows, which is what a reader
          // pressing Copy is asking for.
          onCopy={answer ? () => void copyText(answer) : undefined}
          onRateUp={() => rate(closing.id, 'up')}
          onRateDown={() => rate(closing.id, 'down')}
          onRetry={onRetry && retryableId === closing.id ? onRetry : undefined}
        />,
      );
    }

    rows.push(
      <ConversationTurn
        key={turn.id}
        theme={theme}
        // Not the first turn after a divider, and not a turn whose superseded quote already opened
        // the exchange 16px above it — both are cases CSS cannot find from here.
        spaced={!opensDay && !superseded}
        // `padded={false}` lives inside `UserTurn`: the turn's `.user` slot already carries the 6px.
        user={
          turn.user ? (
            <UserTurn
              message={turn.user}
              theme={theme}
              artifactThumb={artifactThumb}
              mentionNames={mentionNames}
              mentionTarget={mentionTarget}
            />
          ) : undefined
        }
      >
        {/* Undefined rather than an empty array, so a turn still waiting for its reply does not
            paint 6px of an empty assistant row under the bubble. */}
        {blocks.length > 0 ? blocks : undefined}
      </ConversationTurn>,
    );
  });

  return (
    <ConversationColumn theme={theme} width={columnWidth}>{rows}</ConversationColumn>
  );
});
